import React from 'react';
import {Alert, Box, Typography} from '@mui/material';
import {InlineNode, DataItemLinkContext, WikidataSearchProvider} from './SourcesInlineNodes';
import {MentionHoverDismissProvider} from '../../../../../components/entityMentions/MentionHoverDismissContext';

/**
 * Renders a ProseMirror JSON document as a React component tree.
 *
 * Supports bidirectional navigation with the PDF bbox viewer:
 *  - `highlightedBlockKey` — highlights the matching top-level block (by sourceBlockId or index)
 *  - `onBlockClick(blockKey)` — fires when user clicks a block (PM → PDF)
 *  - `ref` — exposes `scrollToBlock(key, pageIndex?)` for PDF → PM navigation
 *
 * The shared key is the server-stamped `attrs.sourceBlockId` (aligned to a model_output
 * block at extraction time); it falls back to the positional block index for documents
 * extracted before anchoring existed.
 */

// ─── Inline content ────────────────────────────────────────────────────────────

// Renders a standalone image node (used both as a top-level block and when an
// image appears inside a paragraph's inline content — e.g. MinerU figure tables
// store table-cell images as `tableCell → paragraph → image`).
function InlineImage({node}) {
    const src = node?.attrs?.src || '';
    const alt = node?.attrs?.alt || '';
    if (!src) return null;
    return (
        <img
            src={src}
            alt={alt}
            style={{
                display: 'block', maxWidth: '100%', height: 'auto',
                margin: '8px 0', borderRadius: 4,
            }}
        />
    );
}

function InlineContent({nodes}) {
    if (!Array.isArray(nodes) || nodes.length === 0) return null;
    return (
        <>
            {nodes.map((node, i) => (
                node?.type === 'image'
                    ? <InlineImage key={i} node={node}/>
                    : <InlineNode key={i} node={node}/>
            ))}
        </>
    );
}

// Renders inline content with the character range [start, end) wrapped in a highlight <mark>,
// so a saved TEXT_RANGE highlight marks just the selected sentence(s) instead of the whole block.
// Text nodes are split at the range boundaries while preserving their ProseMirror marks.
//
// When `matchText` is provided we prefer locating it in the block's concatenated inline text and
// derive offsets from that match — this is robust against whitespace/normalization drift between
// the DOM offsets captured at selection time and the PM JSON text used for rendering. We fall back
// to the stored start/end offsets when the text can't be found.
function InlineContentWithRange({nodes, start, end, matchText}) {
    if (!Array.isArray(nodes) || nodes.length === 0) return null;

    // Concatenate inline text to optionally re-locate the selected text.
    let resolvedStart = start;
    let resolvedEnd = end;
    if (typeof matchText === 'string' && matchText.length > 0) {
        const full = nodes.map((n) => (typeof n?.text === 'string' ? n.text : '')).join('');
        // Prefer an exact match near the stored offset; else first occurrence.
        let idx = full.indexOf(matchText, Math.max(0, (start || 0) - 5));
        if (idx < 0) idx = full.indexOf(matchText);
        if (idx >= 0) {
            resolvedStart = idx;
            resolvedEnd = idx + matchText.length;
        }
    }

    const before = [];
    const inside = [];
    const after = [];
    let pos = 0;
    nodes.forEach((node) => {
        const text = typeof node?.text === 'string' ? node.text : null;
        if (text == null) {
            // Non-text inline node (e.g. image). Place relative to current position.
            if (pos >= resolvedEnd) after.push(node);
            else if (pos < resolvedStart) before.push(node);
            else inside.push(node);
            return;
        }
        const len = text.length;
        const nodeStart = pos;
        const nodeEnd = pos + len;
        pos = nodeEnd;
        // Fully before / after the range.
        if (nodeEnd <= resolvedStart) { before.push(node); return; }
        if (nodeStart >= resolvedEnd) { after.push(node); return; }
        // Overlaps the range — split into up-to-three text slices preserving marks.
        const sliceStart = Math.max(resolvedStart - nodeStart, 0);
        const sliceEnd = Math.min(resolvedEnd - nodeStart, len);
        if (sliceStart > 0) before.push({...node, text: text.slice(0, sliceStart)});
        if (sliceEnd > sliceStart) inside.push({...node, text: text.slice(sliceStart, sliceEnd)});
        if (sliceEnd < len) after.push({...node, text: text.slice(sliceEnd)});
    });
    const render = (arr) => arr.map((node, i) => (
        node?.type === 'image'
            ? <InlineImage key={i} node={node}/>
            : <InlineNode key={i} node={node}/>
    ));
    return (
        <>
            {render(before)}
            <mark
                style={{
                    backgroundColor: 'var(--saved-highlight-bg, rgba(255, 200, 60, 0.45))',
                    borderRadius: 2,
                    padding: '0 1px',
                    boxShadow: 'inset 0 -2px 0 var(--saved-highlight-color, rgba(255,165,0,0.9))',
                    color: 'inherit',
                }}
            >
                {render(inside)}
            </mark>
            {render(after)}
        </>
    );
}

// ─── Block-level nodes ─────────────────────────────────────────────────────────

// Concatenates all descendant text of a node (depth-first), matching the offset space used by
// renderNodeWithRange and by the DOM textContent captured at selection time.
function collectNodeText(node) {
    if (!node) return '';
    if (typeof node.text === 'string') return node.text;
    return (node.content || []).map(collectNodeText).join('');
}

// Recursively renders a block subtree while wrapping the character range [start, end) (measured
// over the subtree's concatenated text) in a highlight <mark>. Works for paragraphs, headings AND
// nested structures like bullet lists, where the highlighted text lives several levels deep.
// `counter` is a shared mutable {pos} so sibling/descendant calls advance the same offset.
function renderNodeWithRange(node, start, end, counter, key) {
    if (!node) return null;

    // Leaf text node: slice into before / inside / after relative to the global range.
    if (typeof node.text === 'string') {
        const text = node.text;
        const nodeStart = counter.pos;
        const nodeEnd = nodeStart + text.length;
        counter.pos = nodeEnd;
        if (nodeEnd <= start || nodeStart >= end) {
            return <InlineNode key={key} node={node}/>; // fully outside range
        }
        const a = Math.max(start - nodeStart, 0);
        const b = Math.min(end - nodeStart, text.length);
        const parts = [];
        if (a > 0) parts.push(<InlineNode key={`${key}-b`} node={{...node, text: text.slice(0, a)}}/>);
        if (b > a) {
            parts.push(
                <mark
                    key={`${key}-m`}
                    style={{
                        backgroundColor: 'var(--saved-highlight-bg, rgba(255, 200, 60, 0.45))',
                        borderRadius: 2,
                        padding: '0 1px',
                        boxShadow: 'inset 0 -2px 0 var(--saved-highlight-color, rgba(255,165,0,0.9))',
                        color: 'inherit',
                    }}
                >
                    <InlineNode node={{...node, text: text.slice(a, b)}}/>
                </mark>,
            );
        }
        if (b < text.length) parts.push(<InlineNode key={`${key}-a`} node={{...node, text: text.slice(b)}}/>);
        return <React.Fragment key={key}>{parts}</React.Fragment>;
    }

    // Inline image / leaf non-text: render normally, consumes no text offset.
    if (node.type === 'image' && (!node.content || node.content.length === 0)) {
        return <InlineImage key={key} node={node}/>;
    }

    // Wrapper node: render the same element type but recurse into children with range marking.
    const children = (node.content || []).map((child, i) => renderNodeWithRange(child, start, end, counter, `${key}-${i}`));
    const Tag = blockTagFor(node);
    if (!Tag) return <React.Fragment key={key}>{children}</React.Fragment>;
    // <table> requires a <tbody> wrapper or browsers silently inject one, causing a hydration mismatch.
    if (Tag === 'table') return <table key={key}><tbody>{children}</tbody></table>;
    return <Tag key={key}>{children}</Tag>;
}

// Maps a PM node type to the DOM tag used when re-rendering a highlighted block subtree.
// Table node types must be included so renderNodeWithRange preserves the table structure
// (missing entries fall through to null → Fragment, collapsing <table>/<tr>/<td> into flat text).
function blockTagFor(node) {
    switch (node?.type) {
        case 'paragraph': return 'p';
        case 'heading': return `h${Math.min(Math.max(node.attrs?.level || 1, 1), 6)}`;
        case 'bulletList':
        case 'bullet_list': return 'ul';
        case 'orderedList':
        case 'ordered_list': return 'ol';
        case 'listItem':
        case 'list_item': return 'li';
        case 'blockquote': return 'blockquote';
        case 'table': return 'table';
        case 'tableRow':
        case 'table_row': return 'tr';
        case 'tableHeader':
        case 'table_header': return 'th';
        case 'tableCell':
        case 'table_cell': return 'td';
        default: return null; // unknown wrapper → fragment (no extra element)
    }
}

// Recursively renders a block subtree with multiple search match ranges highlighted.
// `ranges` = sorted array of {start, end, globalMatchIdx, isActive} (char offsets over the
// full subtree's concatenated text). `counter` = shared mutable {pos} so siblings advance it.
function renderNodeWithSearchMatches(node, ranges, counter, key, activeMatchOffsets) {
    if (!node || !ranges || ranges.length === 0) return null;

    // Leaf text node: split into segments, wrapping any overlapping match range in <mark>.
    if (typeof node.text === 'string') {
        const text = node.text;
        const nodeStart = counter.pos;
        const nodeEnd = nodeStart + text.length;
        counter.pos = nodeEnd;

        // Which ranges overlap this text node?
        const overlapping = ranges.filter((r) => r.end > nodeStart && r.start < nodeEnd);
        if (overlapping.length === 0) {
            return <InlineNode key={key} node={node}/>;
        }

        const parts = [];
        let cur = nodeStart;
        overlapping.forEach((r, ri) => {
            const segStart = Math.max(r.start, nodeStart);
            const segEnd = Math.min(r.end, nodeEnd);
            if (segStart > cur) {
                const before = text.slice(cur - nodeStart, segStart - nodeStart);
                parts.push(<InlineNode key={`${key}-b${ri}`} node={{...node, text: before}}/>);
            }
            if (segEnd > segStart) {
                const inside = text.slice(segStart - nodeStart, segEnd - nodeStart);
                const isActive = activeMatchOffsets && activeMatchOffsets.has(r.globalMatchIdx);
                parts.push(
                    <mark
                        key={`${key}-m${ri}`}
                        data-search-match={r.globalMatchIdx}
                        style={{
                            backgroundColor: isActive
                                ? 'var(--search-match-active-bg, rgba(34, 197, 94, 0.55))'
                                : 'var(--search-match-bg, rgba(134, 239, 172, 0.45))',
                            outline: isActive ? '2px solid var(--search-match-active-ring, rgba(22, 163, 74, 0.9))' : 'none',
                            outlineOffset: '1px',
                            borderRadius: 2,
                            padding: '0 1px',
                            color: 'inherit',
                            fontWeight: isActive ? 700 : 'inherit',
                        }}
                    >
                        <InlineNode node={{...node, text: inside}}/>
                    </mark>,
                );
            }
            cur = segEnd;
        });
        if (cur < nodeEnd) {
            const after = text.slice(cur - nodeStart);
            parts.push(<InlineNode key={`${key}-a`} node={{...node, text: after}}/>);
        }
        return <React.Fragment key={key}>{parts}</React.Fragment>;
    }

    // Inline image / leaf non-text.
    if (node.type === 'image' && (!node.content || node.content.length === 0)) {
        return <InlineImage key={key} node={node}/>;
    }

    // Wrapper node: recurse into children.
    const children = (node.content || []).map((child, i) =>
        renderNodeWithSearchMatches(child, ranges, counter, `${key}-${i}`, activeMatchOffsets),
    );
    const Tag = blockTagFor(node);
    if (!Tag) return <React.Fragment key={key}>{children}</React.Fragment>;
    if (Tag === 'table') return <table key={key}><tbody>{children}</tbody></table>;
    return <Tag key={key}>{children}</Tag>;
}

function PmNode({node, blockIndex, isHighlighted, isHovered, isSavedHighlight, highlightRange, onBlockClick, onBlockHover, onBlockHighlight, isTopLevel, searchBlockMatches, activeSearchMatchIdx}) {
    if (!node) return null;

    // The shared anchor key for PDF↔PM sync: prefer the server-stamped sourceBlockId,
    // fall back to the positional index for docs extracted before anchoring existed.
    const sourceBlockId = node.attrs?.sourceBlockId ?? null;
    const blockKey = sourceBlockId ?? blockIndex;
    // pageIndex from content_list–derived PM docs (attrs.pageIndex); used as a page-scroll
    // fallback when no model_output block matches the blockKey in the PDF viewer.
    const pageIndex = node.attrs?.pageIndex ?? null;

    const handleClick = isTopLevel && onBlockClick
        ? (e) => { e.stopPropagation(); onBlockClick(blockKey, pageIndex); }
        : undefined;

    // Hover correlates panes visually only — no scroll. Enter sets the hover key, leave clears it.
    const handleMouseEnter = isTopLevel && onBlockHover
        ? () => onBlockHover(blockKey, pageIndex)
        : undefined;
    const handleMouseLeave = isTopLevel && onBlockHover
        ? () => onBlockHover(null, pageIndex)
        : undefined;

    const anchorAttrs = isTopLevel ? {
        'data-block-index': blockIndex,
        ...(sourceBlockId != null ? {'data-source-block-id': sourceBlockId} : {}),
        ...(pageIndex != null ? {'data-page-index': pageIndex} : {}),
    } : {};

    const interactiveProps = isTopLevel
        ? {...anchorAttrs, onClick: handleClick, onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave}
        : {};

    // Cable metadata blocks (attrs.cableMeta) get a distinct "metadata panel" treatment: a tinted
    // background + left accent border so consecutive metadata blocks read as a grouped header panel,
    // visually separated from the readable body. The block stays a top-level paragraph/heading so
    // data-block-index, page anchors and entity-mark offsets are unaffected.
    const isCableMeta = node.attrs?.cableMeta === true;
    const cableRole = node.attrs?.cableRole || null;
    const cableMetaStyle = isCableMeta ? {
        backgroundColor: 'var(--cable-meta-bg, rgba(99, 102, 241, 0.06))',
        borderLeft: '3px solid var(--cable-meta-accent, rgba(99, 102, 241, 0.55))',
        padding: '4px 10px',
        margin: 0,
        fontFamily: (cableRole === 'subject')
            ? 'inherit'
            : "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
        fontSize: (cableRole === 'classification' || cableRole === 'subject') ? undefined : '0.85em',
        letterSpacing: cableRole === 'classification' ? '0.04em' : undefined,
        color: 'var(--cable-meta-color, inherit)',
    } : null;

    // Selected block: solid outline + filled bg. Hover-only (not selected): lighter dashed cue so
    // the user can tell a transient hover correlation apart from a committed selection.
    // Saved highlight (from highlight panel): amber left border + light amber bg.
    // When a sub-range (sentence) highlight is active for this block, don't fill the whole block —
    // the inline <mark> carries the highlight instead. Keep the subtle left border as a block cue.
    const hasRange = isSavedHighlight && highlightRange
        && Number.isInteger(highlightRange.startOffset)
        && Number.isInteger(highlightRange.endOffset)
        && highlightRange.endOffset > highlightRange.startOffset;

    // Resolve the highlight range over THIS block's full subtree text. Prefer locating the stored
    // selected text (robust against offset drift and against the same phrase appearing elsewhere in
    // the doc, since we only search within this block); fall back to the raw offsets.
    let resolvedRange = null;
    if (hasRange) {
        const full = collectNodeText(node);
        let s = highlightRange.startOffset;
        let e = highlightRange.endOffset;
        const matchText = highlightRange.text;
        if (typeof matchText === 'string' && matchText.length > 0) {
            let idx = full.indexOf(matchText, Math.max(0, s - 5));
            if (idx < 0) idx = full.indexOf(matchText);
            if (idx >= 0) { s = idx; e = idx + matchText.length; }
        }
        s = Math.max(0, Math.min(s, full.length));
        e = Math.max(0, Math.min(e, full.length));
        if (e > s) resolvedRange = {start: s, end: e};
    }

    const highlightStyle = isHighlighted ? {
        outline: '2px solid',
        outlineColor: 'var(--highlight-color, #1976d2)',
        outlineOffset: '2px',
        borderRadius: 4,
        backgroundColor: 'var(--highlight-bg, rgba(25,118,210,0.08))',
        cursor: 'pointer',
    } : isHovered ? {
        outline: '2px dashed',
        outlineColor: 'var(--hover-color, rgba(220,170,0,0.7))',
        outlineOffset: '2px',
        borderRadius: 4,
        backgroundColor: 'var(--hover-bg, rgba(255,236,60,0.18))',
        cursor: 'pointer',
    } : isSavedHighlight ? (hasRange ? {
        // Sub-range highlight: the inline <mark> is the only cue. No block border/background,
        // so it doesn't visually collide with amber entity-mention underlines.
        ...(isTopLevel && onBlockClick ? {cursor: 'pointer'} : {}),
    } : {
        borderLeft: '3px solid var(--saved-highlight-color, rgba(255,165,0,0.9))',
        paddingLeft: 8,
        borderRadius: 2,
        backgroundColor: 'var(--saved-highlight-bg, rgba(255,200,60,0.28))',
    }) : (isTopLevel && onBlockClick
        ? {...(cableMetaStyle || {}), cursor: 'pointer'}
        : (cableMetaStyle || undefined));

    // Inline content for paragraph/heading: when a range is set, render the sub-range marked.
    const inlineContent = (nodes) => (hasRange
        ? <InlineContentWithRange nodes={nodes} start={highlightRange.startOffset} end={highlightRange.endOffset} matchText={highlightRange.text}/>
        : <InlineContent nodes={nodes}/>);

    // Highlight button: shown on hover for top-level blocks when onBlockHighlight is provided.
    // Note: rendered in PmDocContent's wrapping div, not here, to avoid re-patching every case.

    // Sub-range highlight: render the entire block subtree with [start,end) marked. This single
    // path covers paragraphs, headings AND lists (where highlighted text is nested in list items),
    // so a selected sentence inside a bullet is marked precisely instead of the whole block.
    if (resolvedRange) {
        const counter = {pos: 0};
        const inner = renderNodeWithRange(node, resolvedRange.start, resolvedRange.end, counter, `r${blockIndex}`);
        // renderNodeWithRange already emits the block's own tag; wrap with the block cue + anchors.
        return (
            <div {...interactiveProps} style={highlightStyle}>
                {inner}
            </div>
        );
    }

    // Search highlights: render the entire block subtree with all search match ranges marked.
    // Using renderNodeWithSearchMatches ensures correct offset tracking through nested structures
    // (lists, blockquotes, tables) — the same counter.pos approach as renderNodeWithRange.
    if (searchBlockMatches && searchBlockMatches.length > 0) {
        const sortedMatches = [...searchBlockMatches].sort((a, b) => a.start - b.start);
        const counter = {pos: 0};
        // Build the active-match Set locally here — only blocks that *have* matches ever reach
        // this branch, so we're constructing at most one tiny Set per matched block per render
        // rather than one Set shared across ALL blocks (the old approach re-rendered every
        // VirtualBlock on every navigation step).
        const activeMatchOffsetsLocal = activeSearchMatchIdx != null && activeSearchMatchIdx >= 0
            ? new Set([activeSearchMatchIdx])
            : null;
        const inner = renderNodeWithSearchMatches(node, sortedMatches, counter, `s${blockIndex}`, activeMatchOffsetsLocal);
        return (
            <div {...interactiveProps} style={highlightStyle}>
                {inner}
            </div>
        );
    }

    switch (node.type) {
        case 'doc':
            return (
                <>
                    {(node.content || []).map((child, i) => (
                        <PmNode key={i} node={child} blockIndex={i} isHighlighted={false}
                                onBlockClick={onBlockClick} onBlockHover={onBlockHover} isTopLevel={true}/>
                    ))}
                </>
            );

        case 'paragraph': {
            const pInner = <p {...interactiveProps} style={highlightStyle}>{node.content?.length ? inlineContent(node.content) : <br/>}</p>;
            return pInner;
        }

        case 'heading': {
            const level = Math.min(Math.max(node.attrs?.level || 1, 1), 6);
            const Tag = `h${level}`;
            return (
                <Tag {...interactiveProps} style={highlightStyle}>
                    {inlineContent(node.content)}
                </Tag>
            );
        }

        case 'bulletList':
        case 'bullet_list':
            return (
                <ul {...interactiveProps} style={highlightStyle}>
                    {(node.content || []).map((child, i) => (
                        <PmNode key={i} node={child}/>
                    ))}
                </ul>
            );

        case 'orderedList':
        case 'ordered_list':
            return (
                <ol {...interactiveProps} style={highlightStyle}>
                    {(node.content || []).map((child, i) => (
                        <PmNode key={i} node={child}/>
                    ))}
                </ol>
            );

        case 'listItem':
        case 'list_item':
            return (
                <li>
                    {(node.content || []).map((child, i) => <PmNode key={i} node={child}/>)}
                </li>
            );

        case 'blockquote':
            return (
                <blockquote {...interactiveProps} style={highlightStyle}>
                    {(node.content || []).map((child, i) => <PmNode key={i} node={child}/>)}
                </blockquote>
            );

        case 'codeBlock':
        case 'code_block':
            return (
                <pre {...interactiveProps} style={highlightStyle}>
                    <code><InlineContent nodes={node.content}/></code>
                </pre>
            );

        case 'hardBreak':
        case 'hard_break':
            return <br/>;

        case 'horizontalRule':
        case 'horizontal_rule':
            return <hr/>;

        case 'image': {
            const src = node.attrs?.src || '';
            const alt = node.attrs?.alt || '';
            if (!src) return null;
            return (
                <img
                    src={src}
                    alt={alt}
                    {...interactiveProps}
                    style={{
                        display: 'block', maxWidth: '100%', height: 'auto',
                        margin: '8px 0', borderRadius: 4,
                        ...(highlightStyle || {}),
                    }}
                />
            );
        }

        case 'table':
            return (
                <table {...interactiveProps} style={{
                    width: '100%', borderCollapse: 'collapse', margin: '12px 0',
                    ...(highlightStyle || {}),
                }}>
                    <tbody>
                        {(node.content || []).map((child, i) => <PmNode key={i} node={child}/>)}
                    </tbody>
                </table>
            );

        case 'tableRow':
        case 'table_row':
            return <tr>{(node.content || []).map((child, i) => <PmNode key={i} node={child}/>)}</tr>;

        case 'tableCell':
        case 'table_cell':
        case 'tableHeader':
        case 'table_header': {
            const Tag = (node.type === 'tableHeader' || node.type === 'table_header') ? 'th' : 'td';
            return (
                <Tag style={{border: '1px solid #ddd', padding: '6px 8px'}}>
                    {(node.content || []).map((child, i) => <PmNode key={i} node={child}/>)}
                </Tag>
            );
        }

        case 'text':
            return <InlineNode node={node}/>;

        case 'map': {
            const thumb = node.attrs?.snapshotThumbUrl || null;
            const title = node.attrs?.title || 'Map';
            return (
                <div
                    {...interactiveProps}
                    style={{
                        border: '1px solid #d1d5db',
                        borderRadius: 8,
                        overflow: 'hidden',
                        margin: '8px 0',
                        ...(highlightStyle || {}),
                    }}
                >
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '5px 10px',
                        background: '#fff',
                        borderBottom: '1px solid #e8ebed',
                        fontSize: 13,
                        fontWeight: 500,
                        color: '#1a202c',
                    }}>
                        <span role="img" aria-label="Map" style={{marginRight: 6}}>🗺️</span>
                        {title}
                    </div>
                    {thumb ? (
                        <img
                            src={thumb}
                            alt={title}
                            style={{width: '100%', height: 280, objectFit: 'cover', display: 'block'}}
                        />
                    ) : (
                        <div style={{
                            width: '100%',
                            height: 280,
                            background: '#e5e7eb',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#9ca3af',
                            fontSize: 13,
                        }}>
                            Map preview unavailable
                        </div>
                    )}
                </div>
            );
        }

        default:
            if (node.content?.length) {
                return (
                    <>{node.content.map((child, i) => <PmNode key={i} node={child}/>)}</>
                );
            }
            return null;
    }
}

// ─── Top-level component ───────────────────────────────────────────────────────

/**
 * Renders a ProseMirror doc with optional bbox highlighting / click callbacks.
 *
 * @param {object}        content                  - ProseMirror JSON doc node
 * @param {string|number} [highlightedBlockKey]     - shared sourceBlockId (or legacy block index) to highlight
 * @param {Array<number>} [highlightedSegmentIds]   - saved-highlight segment ids; blocks whose sourceBlockId
 *                                                    is in this set receive the yellow highlight style
 * @param {function}      [onBlockClick]            - called with (blockKey) when user clicks a top-level block
 * @param {object}        [linkContext]             - when provided ({referenceId, userId, enabled}), enables
 *                                                    manual Wikidata linking of unlinked mentions in this doc
 *                                                    (Data Items viewer; only the English/enrichment doc).
 */
const ReadonlyProseMirrorRenderer = React.forwardRef(({content, highlightedBlockKey, hoverBlockKey, highlightedSegmentIds, highlightedRanges, onBlockClick, onBlockHover, onBlockHighlight, dir = 'ltr', linkContext = null, searchMatches = null, activeSearchMatchIdx = -1}, ref) => {
    const containerRef = React.useRef(null);
    const [renderError, setRenderError] = React.useState(null);

    // Resolve a block element by its shared key — prefer the stable sourceBlockId anchor,
    // fall back to the positional index for docs extracted before anchoring existed.
    // NOTE: data-source-block-id is now stamped on the outer VirtualBlock wrapper div
    // (always present) as well as on the inner PmNode element, so this works even when
    // the block is off-screen and not yet rendered.
    const findBlockEl = React.useCallback((key) => {
        const root = containerRef.current;
        if (!root || key == null) return null;
        const safe = (typeof window !== 'undefined' && window.CSS && window.CSS.escape)
            ? window.CSS.escape(String(key))
            : String(key).replace(/"/g, '\\"');
        return root.querySelector(`[data-source-block-id="${safe}"]`)
            || root.querySelector(`[data-block-index="${safe}"]`);
    }, []);

    React.useImperativeHandle(ref, () => ({
        // pageIndex is a fallback: content_list–derived PM docs have sourceBlockId based on
        // content_list positions whereas PDF bbox clicks provide model_output blockIds —
        // different ID spaces. When the exact key is not found, scroll to the first PM block
        // on the same page using the data-page-index DOM attribute.
        //
        // VirtualBlock is lazy — an off-screen block is a placeholder <span> until it
        // becomes visible.  When the block IS virtualised, the outer wrapper div now carries
        // data-source-block-id so findBlockEl finds it; we then use a two-step rAF so the
        // IntersectionObserver can fire and mount the real PmNode content before we scroll.
        scrollToBlock(key, pageIndex) {
            const el = findBlockEl(key);
            if (el) {
                // First scroll: bring the element into view (may still be a placeholder).
                el.scrollIntoView({behavior: 'smooth', block: 'center'});
                // Second pass after paint: if the block was virtualised the IO fires and
                // the placeholder becomes real content during this frame; re-centre so the
                // expanded block is properly centred rather than half-off-screen.
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        const el2 = findBlockEl(key);
                        el2?.scrollIntoView({behavior: 'smooth', block: 'center'});
                    });
                });
                return;
            }
            // No exact match — fall back to the first block on the same page.
            if (pageIndex != null) {
                const root = containerRef.current;
                const pageEl = root?.querySelector(`[data-page-index="${pageIndex}"]`);
                pageEl?.scrollIntoView({behavior: 'smooth', block: 'start'});
            }
        },
    }));

    // Scroll when highlighted key changes (PDF → PM direction).
    // Uses a double-rAF: first pass brings the (possibly placeholder) block into view;
    // after two frames the IO may have fired and mounted real content so we re-centre.
    React.useEffect(() => {
        if (highlightedBlockKey == null) return;
        const el = findBlockEl(highlightedBlockKey);
        if (!el) return;
        el.scrollIntoView({behavior: 'smooth', block: 'center'});
        const raf1 = requestAnimationFrame(() => {
            const raf2 = requestAnimationFrame(() => {
                const el2 = findBlockEl(highlightedBlockKey);
                el2?.scrollIntoView({behavior: 'smooth', block: 'center'});
            });
            return () => cancelAnimationFrame(raf2);
        });
        return () => cancelAnimationFrame(raf1);
    }, [highlightedBlockKey, findBlockEl]);

    React.useEffect(() => { setRenderError(null); }, [content]);

    if (!content) return null;
    if (renderError) return <Alert severity="error">{renderError}</Alert>;

    return (
        <ErrorBoundary onError={setRenderError}>
            <MentionHoverDismissProvider>
            <Box
                ref={containerRef}
                aria-readonly="true"
                dir={dir}
                sx={{
                    '--highlight-color': 'rgba(255, 200, 0, 0.8)',
                    '--highlight-bg':    'rgba(255, 236, 60, 0.35)',
                    '--hover-color':     'rgba(220, 170, 0, 0.7)',
                    '--hover-bg':        'rgba(255, 236, 60, 0.18)',
                    '--saved-highlight-color': 'rgba(255, 165, 0, 0.9)',
                    '--saved-highlight-bg':    'rgba(255, 200, 60, 0.28)',
                    '--cable-meta-bg':     'rgba(99, 102, 241, 0.06)',
                    '--cable-meta-accent': 'rgba(99, 102, 241, 0.55)',
                    '--cable-meta-color':  'rgba(55, 65, 81, 0.95)',
                    '--search-match-bg':         'rgba(134, 239, 172, 0.45)',
                    '--search-match-active-bg':  'rgba(34, 197, 94, 0.55)',
                    '--search-match-active-ring': 'rgba(22, 163, 74, 0.9)',
                    color: 'text.primary',
                    lineHeight: 1.65,
                    wordBreak: 'break-word',
                    textAlign: dir === 'rtl' ? 'right' : 'left',
                    '& > :first-of-type': {mt: 0},
                    '& > :last-child': {mb: 0},
                    '& p': {my: 1.5},
                    '& h1, & h2, & h3, & h4, & h5, & h6': {mt: 2.5, mb: 1.25, lineHeight: 1.25},
                    '& img': {display: 'block', maxWidth: '100%', height: 'auto', my: 2, borderRadius: 1},
                    '& a': {color: 'primary.main', textDecoration: 'underline'},
                    '& ul, & ol': {pl: 3, my: 1.5},
                    '& blockquote': {borderLeft: 3, borderColor: 'divider', pl: 2, ml: 0, color: 'text.secondary'},
                    '& table': {width: '100%', borderCollapse: 'collapse', my: 2},
                    '& th, & td': {border: '1px solid', borderColor: 'divider', p: 1},
                    '& pre': {bgcolor: 'action.hover', p: 1.5, borderRadius: 1, overflowX: 'auto', my: 1.5},
                    '& code': {fontFamily: 'monospace', fontSize: '0.875em'},
                }}
            >
                <DataItemLinkContext.Provider value={linkContext}>
                    <WikidataSearchProvider>
                        <PmDocContent
                            content={content}
                            highlightedBlockKey={highlightedBlockKey}
                            hoverBlockKey={hoverBlockKey}
                            highlightedSegmentIds={highlightedSegmentIds}
                            highlightedRanges={highlightedRanges}
                            onBlockClick={onBlockClick}
                            onBlockHover={onBlockHover}
                            onBlockHighlight={onBlockHighlight}
                            searchMatches={searchMatches}
                            activeSearchMatchIdx={activeSearchMatchIdx}
                        />
                    </WikidataSearchProvider>
                </DataItemLinkContext.Provider>
            </Box>
            </MentionHoverDismissProvider>
        </ErrorBoundary>
    );
});

ReadonlyProseMirrorRenderer.displayName = 'ReadonlyProseMirrorRenderer';

// A visual page break in the extracted-text pane. The PM document is a continuous flow, but every
// top-level block carries attrs.pageIndex, so we can re-introduce the source PDF's page boundaries
// purely for reading orientation (and as a page-level scroll target via data-page-index).
function PageDivider({pageIndex}) {
    return (
        <Box
            data-page-index={pageIndex}
            contentEditable={false}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                my: 3,
                userSelect: 'none',
                color: 'text.disabled',
                '&::before, &::after': {
                    content: '""',
                    flex: 1,
                    borderTop: '1px dashed',
                    borderColor: 'divider',
                },
            }}
        >
            <Typography variant="caption" sx={{whiteSpace: 'nowrap', fontWeight: 600, letterSpacing: 0.5}}>
                Page {pageIndex + 1}
            </Typography>
        </Box>
    );
}

// Approximate rendered size of the floating "★ Highlight" button (must match button styles below).
const HIGHLIGHT_BTN_WIDTH = 104;
const HIGHLIGHT_BTN_HEIGHT = 28;
const HIGHLIGHT_BTN_GAP = 6;

/** First-line rect for multi-line selections; falls back to the range bounding box. */
function selectionAnchorRect(range) {
    const lineRects = range.getClientRects();
    return lineRects.length > 0 ? lineRects[0] : range.getBoundingClientRect();
}

/** Position the highlight button above the anchor line, fully inside the container. */
function highlightButtonPos(anchorRect, container) {
    const cRect = container.getBoundingClientRect();
    const pad = 4;
    const top = anchorRect.top - cRect.top + container.scrollTop - HIGHLIGHT_BTN_HEIGHT - HIGHLIGHT_BTN_GAP;
    const preferredLeft = anchorRect.right - cRect.left + container.scrollLeft - 12;
    const left = Math.max(
        pad,
        Math.min(preferredLeft, container.clientWidth - HIGHLIGHT_BTN_WIDTH - pad),
    );
    return {top, left};
}

// Renders the top-level blocks of a doc, wiring up highlight, hover, and click.
// Inserts a PageDivider whenever consecutive blocks cross a pageIndex boundary so the
// continuous PM flow visually mirrors the source PDF's pagination.
//
// VIRTUALIZATION: top-level blocks are rendered lazily — only blocks within
// VIRTUAL_MARGIN_PX of the scroll container are mounted; distant blocks are replaced
// by a height-preserving placeholder. This keeps DOM nodes O(viewport) rather than
// O(document), which is critical for large PDFs (tens of thousands of PM blocks).
//
// PERF: Keep this tight. 800px was causing all placeholder blocks within a full screen-height
// panel to immediately become visible on load — mounting all content simultaneously and
// generating 80K+ DOM nodes. 400px limits the eager zone to roughly 2 screen heights.
const VIRTUAL_MARGIN_PX = 400;

// Estimated height (px) for a placeholder block. Used to compute `minHeight` when a block
// is not yet visible so that distant blocks are physically positioned outside the IO margin,
// preventing a "fire everything visible at once" problem on first mount.
// 48px ≈ one line of paragraph text at 1.65 line-height + a small vertical margin.
const PLACEHOLDER_HEIGHT_PX = 48;

// A single block entry rendered in PmDocContent. Handles its own visibility via
// IntersectionObserver so it only mounts heavy inner content when near the viewport.
const VirtualBlock = React.memo(function VirtualBlock({
    child, i, pageIndex, showDivider, isHighlighted, isHovered,
    isSavedHighlight, highlightRange, isFirstHighlight, firstHighlightRef,
    onBlockClick, onBlockHover, onBlockHighlight, scrollRootRef,
    searchBlockMatches, activeSearchMatchIdx, hasSearchMatch,
}) {
    const elRef = React.useRef(null);
    // Force-visible when highlighted/hovered/saved-highlight so it always renders the
    // real content (not the placeholder) even if out of viewport.
    // Also force-visible when it contains a search match so the <mark> is in the DOM
    // for the scrollIntoView query to find it.
    const forceVisible = isHighlighted || isHovered || isSavedHighlight || isFirstHighlight || hasSearchMatch;
    const [visible, setVisible] = React.useState(forceVisible);

    React.useEffect(() => {
        if (forceVisible) { setVisible(true); return; }
        const el = elRef.current;
        if (!el) return;
        // Read scrollRootRef.current at creation time — the ref was set via useLayoutEffect
        // in PmDocContent (parent) which runs before this useEffect fires in children.
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) setVisible(true);
                else setVisible(false);
            },
            {root: scrollRootRef?.current ?? null, rootMargin: `${VIRTUAL_MARGIN_PX}px 0px`},
        );
        observer.observe(el);
        return () => observer.disconnect();
    // scrollRootRef is a stable ref object — it never changes identity, so this effect
    // runs exactly once per block (on mount) with the already-resolved root.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [forceVisible]);

    const childKey = child?.attrs?.sourceBlockId ?? i;
    const handleDivClick = onBlockClick
        ? (e) => {
            if (e.target === e.currentTarget) onBlockClick(childKey, pageIndex);
        }
        : undefined;

    // Derive stable anchor attributes from the child node so the outer wrapper div always
    // carries data-block-index, data-source-block-id, and data-page-index — even when the
    // block is virtualised (not yet rendered).  scrollToBlock relies on these attributes to
    // scroll off-screen blocks into view; without them it falls back to PageDivider elements
    // which point to the wrong location in the extracted-text pane.
    const outerSourceBlockId = child?.attrs?.sourceBlockId ?? null;
    const outerPageIndex = child?.attrs?.pageIndex ?? null;

    return (
        <>
            {showDivider && <PageDivider pageIndex={pageIndex}/> }
            <div
                ref={(node) => {
                    elRef.current = node;
                    if (isFirstHighlight && firstHighlightRef) firstHighlightRef.current = node;
                }}
                data-block-index={i}
                {...(outerSourceBlockId != null ? {'data-source-block-id': outerSourceBlockId} : {})}
                {...(outerPageIndex != null ? {'data-page-index': outerPageIndex} : {})}
                style={{position: 'relative', minHeight: visible ? undefined : PLACEHOLDER_HEIGHT_PX}}
                onClick={handleDivClick}
            >
                {visible ? (
                    <PmNode
                        node={child}
                        blockIndex={i}
                        isHighlighted={isHighlighted}
                        isHovered={isHovered}
                        isSavedHighlight={isSavedHighlight}
                        highlightRange={highlightRange}
                        onBlockClick={onBlockClick}
                        onBlockHover={onBlockHover}
                        onBlockHighlight={onBlockHighlight}
                        isTopLevel={true}
                        searchBlockMatches={searchBlockMatches}
                        activeSearchMatchIdx={activeSearchMatchIdx}
                    />
                ) : (
                    // Placeholder: preserve approximate height so the scrollbar thumb doesn't
                    // jump when blocks mount/unmount. PLACEHOLDER_HEIGHT_PX keeps distant blocks
                    // physically outside the IO margin, preventing a mass-visibility storm.
                    <span style={{display: 'block', height: PLACEHOLDER_HEIGHT_PX, opacity: 0}} aria-hidden="true"/>
                )}
            </div>
        </>
    );
});

function PmDocContent({content, highlightedBlockKey, hoverBlockKey, highlightedSegmentIds, highlightedRanges, onBlockClick, onBlockHover, onBlockHighlight, searchMatches, activeSearchMatchIdx}) {
    // Build a fast lookup set of highlighted block indices (the active highlight's region
    // numberInItem values). Must be before any conditional return to satisfy rules-of-hooks.
    const savedSegmentSet = React.useMemo(() => {
        if (!Array.isArray(highlightedSegmentIds) || highlightedSegmentIds.length === 0) return null;
        return new Set(highlightedSegmentIds.map(String));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [highlightedSegmentIds?.join(',')]);

    // Build per-block search match maps. Each entry: blockIndex → [{start, end, globalMatchIdx}].
    // We no longer build a "force-visible" Set for all matched blocks — forcing hundreds of blocks
    // visible simultaneously causes a React render storm that saturates the main thread on large PDFs.
    // Instead only the active match block (and its immediate neighbours) are forced visible; all
    // other matched blocks render lazily via IntersectionObserver and will show marks as they
    // scroll into view.
    const {searchMatchesByBlock} = React.useMemo(() => {
        if (!Array.isArray(searchMatches) || searchMatches.length === 0) {
            return {searchMatchesByBlock: null};
        }
        const byBlock = {};
        searchMatches.forEach((m, globalMatchIdx) => {
            if (!byBlock[m.blockIndex]) byBlock[m.blockIndex] = [];
            byBlock[m.blockIndex].push({...m, globalMatchIdx});
        });
        return {searchMatchesByBlock: byBlock};
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchMatches]);

    // The block index of the currently focused match. Only this block (and ±1 neighbour) is
    // force-mounted so the <mark> element exists in the DOM for scrollIntoView.
    const activeMatchBlockIdx = React.useMemo(() => {
        if (activeSearchMatchIdx == null || activeSearchMatchIdx < 0 || !Array.isArray(searchMatches)) return -1;
        return searchMatches[activeSearchMatchIdx]?.blockIndex ?? -1;
    }, [searchMatches, activeSearchMatchIdx]);

    // Scroll the first highlighted block into view when the active highlight changes.
    const firstHighlightRef = React.useRef(null);
    React.useEffect(() => {
        if (firstHighlightRef.current) {
            firstHighlightRef.current.scrollIntoView({behavior: 'smooth', block: 'center'});
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [highlightedSegmentIds?.join(',')]);

    // Scroll the active search match into view when activeSearchMatchIdx changes.
    // Single rAF is not enough: when the target block is off-screen, VirtualBlock
    // needs a full React render cycle (state update → re-render → paint) before the
    // <mark> element exists in the DOM. We poll with rAF until it appears.
    //
    // We use direct scrollTop manipulation rather than scrollIntoView so we always
    // target the correct scroll container (the element's nearest scrollable ancestor)
    // and are not confused by nested containers in dual-pane layouts. After the initial
    // scroll we run STABILIZE_PASSES re-checks: VirtualBlocks above the match may mount
    // (placeholder 1.65em → real content) and push the match down; each pass re-centres
    // if the target drifted outside DRIFT_THRESHOLD_PX of the container's centre.
    React.useEffect(() => {
        if (activeSearchMatchIdx == null || activeSearchMatchIdx < 0) return;
        let cancelled = false;
        let attempts = 0;
        const MAX_ATTEMPTS = 20;
        const STABILIZE_PASSES = 5;
        const DRIFT_THRESHOLD_PX = 30;

        // Walk up the DOM from `el` to find the nearest element that can scroll vertically.
        function findScrollContainer(el) {
            let node = el.parentElement;
            while (node && node !== document.body) {
                const {overflowY, overflow} = window.getComputedStyle(node);
                if (overflowY === 'auto' || overflowY === 'scroll'
                    || overflow === 'auto' || overflow === 'scroll') {
                    return node;
                }
                node = node.parentElement;
            }
            return null; // fallback: viewport
        }

        // Centre `el` inside its scroll container by setting scrollTop directly.
        function centreInContainer(el) {
            const container = findScrollContainer(el);
            if (!container) {
                el.scrollIntoView({behavior: 'instant', block: 'center'});
                return;
            }
            const containerRect = container.getBoundingClientRect();
            const elRect = el.getBoundingClientRect();
            // Offset of el's top within the container's scroll content.
            const elOffsetTop = elRect.top - containerRect.top + container.scrollTop;
            const target = elOffsetTop - container.clientHeight / 2 + elRect.height / 2;
            container.scrollTop = Math.max(0, target);
        }

        // Check if `el` has drifted more than DRIFT_THRESHOLD_PX from its container's centre.
        function hasDrifted(el) {
            const container = findScrollContainer(el);
            if (!container) {
                const rect = el.getBoundingClientRect();
                return Math.abs((rect.top + rect.height / 2) - window.innerHeight / 2) > DRIFT_THRESHOLD_PX;
            }
            const containerRect = container.getBoundingClientRect();
            const elRect = el.getBoundingClientRect();
            const elMidInContainer = elRect.top - containerRect.top + elRect.height / 2;
            const containerMid = container.clientHeight / 2;
            return Math.abs(elMidInContainer - containerMid) > DRIFT_THRESHOLD_PX;
        }

        function stabilize(pass) {
            if (cancelled || pass >= STABILIZE_PASSES) return;
            requestAnimationFrame(() => {
                if (cancelled) return;
                const el = document.querySelector(`[data-search-match="${activeSearchMatchIdx}"]`);
                if (el && hasDrifted(el)) centreInContainer(el);
                stabilize(pass + 1);
            });
        }

        function attempt() {
            if (cancelled) return;
            attempts += 1;
            const el = document.querySelector(`[data-search-match="${activeSearchMatchIdx}"]`);
            if (el) {
                centreInContainer(el);
                stabilize(0);
                return;
            }
            if (attempts < MAX_ATTEMPTS) requestAnimationFrame(attempt);
        }

        const handle = requestAnimationFrame(attempt);
        return () => {
            cancelled = true;
            cancelAnimationFrame(handle);
        };
    }, [activeSearchMatchIdx]);

    // Floating "Add highlight" button anchored to the current text selection. Replaces the old
    // per-block top-right button so the affordance appears right next to what the user selected.
    const containerRef = React.useRef(null);
    const [selBtn, setSelBtn] = React.useState(null); // {top, left, blockIndex} | null
    const canHighlight = typeof onBlockHighlight === 'function';

    // The scroll container for IntersectionObserver virtualization.
    // PERF: Use a ref (not state) so there is no re-render when the root is resolved.
    // If we used React state here, the subsequent state update would cause ALL VirtualBlocks
    // to re-run their effects simultaneously (disconnect + re-create 1000 IO instances) which
    // is the same mass-IO-churn problem we fixed in PdfPageWithOverlay.
    //
    // useLayoutEffect runs synchronously after DOM mutations but BEFORE useEffect fires in
    // children. This guarantees that scrollRootRef.current is set before any VirtualBlock's
    // useEffect creates its IntersectionObserver — each block gets the correct root on the
    // first (and only) IO creation, with no re-render cycle.
    const scrollRootRef = React.useRef(null);
    React.useLayoutEffect(() => {
        let el = containerRef.current?.parentElement;
        while (el) {
            const {overflowY} = window.getComputedStyle(el);
            if (overflowY === 'auto' || overflowY === 'scroll') {
                scrollRootRef.current = el;
                return;
            }
            el = el.parentElement;
        }
        scrollRootRef.current = null; // viewport fallback
    }, []);

    React.useEffect(() => {
        if (!canHighlight) return undefined;
        const container = containerRef.current;
        if (!container) return undefined;

        const resolveBlockEl = (node) => {
            let el = node?.nodeType === Node.TEXT_NODE ? node.parentElement : node;
            while (el && el !== container) {
                if (el.dataset && el.dataset.blockIndex != null) return el;
                el = el.parentElement;
            }
            return null;
        };

        const offsetWithinBlock = (blockEl, node, nodeOffset, fallbackToEnd) => {
            if (!node || !blockEl.contains(node)) {
                return fallbackToEnd ? blockEl.textContent.length : 0;
            }
            const r = document.createRange();
            r.selectNodeContents(blockEl);
            r.setEnd(node, nodeOffset);
            return r.toString().length;
        };

        const updateFromSelection = () => {
            const sel = window.getSelection();
            if (!sel || sel.isCollapsed || sel.rangeCount === 0) {
                setSelBtn(null);
                return;
            }
            const range = sel.getRangeAt(0);
            if (!container.contains(range.commonAncestorContainer)) {
                setSelBtn(null);
                return;
            }
            const rect = range.getBoundingClientRect();
            if (!rect || (rect.width === 0 && rect.height === 0)) {
                setSelBtn(null);
                return;
            }
            const blockEl = resolveBlockEl(range.startContainer) || resolveBlockEl(sel.focusNode);
            if (!blockEl) {
                setSelBtn(null);
                return;
            }
            const blockIndex = Number(blockEl.dataset.blockIndex);
            if (!Number.isInteger(blockIndex)) {
                setSelBtn(null);
                return;
            }
            const blockLen = blockEl.textContent.length;
            let startOffset = offsetWithinBlock(blockEl, range.startContainer, range.startOffset, false);
            let endOffset = offsetWithinBlock(blockEl, range.endContainer, range.endOffset, true);
            if (endOffset < startOffset) { const t = startOffset; startOffset = endOffset; endOffset = t; }
            startOffset = Math.max(0, Math.min(startOffset, blockLen));
            endOffset = Math.max(0, Math.min(endOffset, blockLen));
            if (endOffset <= startOffset) {
                setSelBtn(null);
                return;
            }
            const blockText = blockEl.textContent;
            const text = blockText.slice(startOffset, endOffset);
            const anchorRect = selectionAnchorRect(range);
            const {top, left} = highlightButtonPos(anchorRect, container);
            setSelBtn({top, left, blockIndex, startOffset, endOffset, text});
        };

        const onMouseUp = () => window.setTimeout(updateFromSelection, 0);
        const onSelectionChange = () => {
            const sel = window.getSelection();
            if (!sel || sel.isCollapsed) setSelBtn(null);
        };
        document.addEventListener('mouseup', onMouseUp);
        document.addEventListener('selectionchange', onSelectionChange);
        return () => {
            document.removeEventListener('mouseup', onMouseUp);
            document.removeEventListener('selectionchange', onSelectionChange);
        };
    }, [canHighlight]);

    if (!content) return null;

    if (content.type === 'doc') {
        const children = content.content || [];
        const hasPageInfo = children.some((c) => Number.isInteger(c?.attrs?.pageIndex));
        // Pre-compute per-block metadata outside JSX so VirtualBlock receives stable props.
        const firstHighlightIdx = savedSegmentSet
            ? Math.min(...Array.from(savedSegmentSet, Number).filter(Number.isFinite))
            : -1;

        let prevPage = null;
        const blocks = children.map((child, i) => {
            const pageIndex = hasPageInfo && Number.isInteger(child?.attrs?.pageIndex)
                ? child.attrs.pageIndex : null;
            const needsDivider = hasPageInfo && pageIndex != null && pageIndex !== prevPage;
            if (needsDivider) prevPage = pageIndex;
            const childKey = child?.attrs?.sourceBlockId ?? i;
            const isHighlighted = highlightedBlockKey != null
                && (highlightedBlockKey === childKey || highlightedBlockKey === i);
            const isHovered = hoverBlockKey != null
                && (hoverBlockKey === childKey || hoverBlockKey === i);
            const isSavedHighlight = savedSegmentSet != null && savedSegmentSet.has(String(i));
            const isFirstHighlight = isSavedHighlight && firstHighlightIdx === i;
            const blockRange = (isSavedHighlight && highlightedRanges && highlightedRanges[i]) || null;
            const blockSearchMatches = searchMatchesByBlock ? (searchMatchesByBlock[i] || null) : null;
            return (
                <VirtualBlock
                    key={i}
                    child={child}
                    i={i}
                    pageIndex={pageIndex}
                    showDivider={needsDivider}
                    isHighlighted={isHighlighted}
                    isHovered={isHovered}
                    isSavedHighlight={isSavedHighlight}
                    highlightRange={blockRange}
                    isFirstHighlight={isFirstHighlight}
                    firstHighlightRef={isFirstHighlight ? firstHighlightRef : null}
                    onBlockClick={onBlockClick}
                    onBlockHover={onBlockHover}
                    onBlockHighlight={onBlockHighlight}
                    scrollRootRef={scrollRootRef}
                    searchBlockMatches={blockSearchMatches}
                    activeSearchMatchIdx={activeSearchMatchIdx}
                    hasSearchMatch={
                        activeMatchBlockIdx >= 0 &&
                        Math.abs(i - activeMatchBlockIdx) <= 1
                    }
                />
            );
        });

        return (
            <div ref={containerRef} style={{position: 'relative'}}>
                {blocks}
                {canHighlight && selBtn && (
                    <button
                        type="button"
                        title="Add highlight"
                        onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onBlockHighlight({
                                blockIndex: selBtn.blockIndex,
                                startOffset: selBtn.startOffset,
                                endOffset: selBtn.endOffset,
                                text: selBtn.text,
                            });
                            setSelBtn(null);
                            window.getSelection()?.removeAllRanges();
                        }}
                        style={{
                            position: 'absolute',
                            top: selBtn.top,
                            left: selBtn.left,
                            height: HIGHLIGHT_BTN_HEIGHT,
                            padding: '0 10px',
                            border: 'none',
                            borderRadius: 14,
                            background: 'rgba(255,140,0,0.97)',
                            color: '#fff',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            fontSize: 12,
                            fontWeight: 600,
                            lineHeight: 1,
                            whiteSpace: 'nowrap',
                            zIndex: 30,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                        }}
                    >
                        ★ Highlight
                    </button>
                )}
            </div>
        );
    }
    return <PmNode node={content}/>;
}

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {hasError: false};
    }
    static getDerivedStateFromError() { return {hasError: true}; }
    componentDidCatch(error) {
        console.error('[ReadonlyProseMirrorRenderer] Render error:', error);
        this.props.onError?.(error?.message || 'Failed to render document.');
    }
    render() {
        if (this.state.hasError) {
            // Avoid a blank pane while the parent picks up onError and re-renders the Alert.
            return (
                <Typography variant="body2" color="error" sx={{p: 2}}>
                    Failed to render document.
                </Typography>
            );
        }
        return this.props.children;
    }
}

export default ReadonlyProseMirrorRenderer;
